import React from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Header,
  Image,
  Label,
} from 'semantic-ui-react';
import { useAuth } from '../providers';

const Commands = () => {
  const { authenticated, user } = useAuth();

  return (
    <React.Fragment>
      <Header as='h2' inverted>Commands</Header>
      <p>
        The Twitching of Isaac is by default configured to allow Twitch chat to interact with the streamers game. This interaction is done via commands
        entered in the Twitch chat interface. The mod has various commands built in by default, which can be seen at <Link to='/help'>the help page</Link>.
      </p>
      <p>
        The default configuration for all commands is that they are set to be enabled for all users, with a default cool down limit of 5 times every 15 seconds per user.
      </p>
      <Header as='h3' inverted>Sharing your help page with chat</Header>
      <p>
        As a registered user of the mod, you have a personalized help page, that will be customized based on how you configure your commands. If you happen to disable commands, or
        make some only available to Subscribers, or utilize Aliasing to rename a command, it is useful to link users to this customized help page.
      </p>
      <p>
        Your chat can enter <strong>!help</strong> as one of the commands in chat, to be served the personalized link. In the event that your chat is in Follower only mode, or, if
        you have a mod preventing posting links in chat, this command may not be useful. There are various options around this:
      </p>
      <ul>
        <li>You can add the <strong>twitchingofisaac</strong> bot to a whitelist to allow it to post links in your chat.</li>
        <li>You can give the <strong>twitchingofisaac</strong> bot moderator status in your channel -- though be aware of the risks of giving random bots this access.</li>
        <li>You can create your own command to link to the personalized (or global) help page via one of your existing bots.</li>
      </ul>
      {authenticated ?
        <p>
          The link to your personalized help page is: <Label>https://twitchingofisaac.com/help/{user.username}</Label>
        </p>
      : ''}
      <Header as='h3' inverted>Configuring Commands</Header>
      <p>
        You can configure commands in two ways. The most straight forward way is utilizing the in-game Mod Menu -- this menu gives you instant access to commands, however is limited
        in how much you can customize each command. The second way, is utilizing this website and accessing the <Link to='/dashboard/commands'>Commands</Link> section from your
        {' '}<Link to='/dashboard'>Dashboard</Link>. From the Commands section, you can perform advanced configuration of each command, with far more options compared to the Mod Menu.
      </p>
      <p>
        For each command listed on the Commands page, you will see a <Button icon='wrench' mini /> button on the right hand side. Clicking this button will bring up the configuration window
        for the selected command.
      </p>
      <Image src='/images/support_command_config.png' />
      <br />
      <p>
        All commands will have the base set of configurable options, which include Enabling/Disabling the command, putting the command in to Subscriber Only mode, setting an alias for
        the command, and configuring a rate limit.
      </p>
      <p>
        Aliases allows you to rename the command for your channel. This can be useful if you want to give it a different name than the default name, or, if you want to localize a command
        to your channels language.
      </p>
      <p>
        Rate limits are per user, per channel - and can be configured as low as once every 59 hours, or as frequent as 50 times per second. The default limits of 5 times within 15 seconds are
        a sane default that prevent the rate limit from generally being hit.
      </p>
      <p>
        <strong>Note:</strong> The <strong>!repeat</strong> command does not circumvent the rate limit! If you set !repeat to allow up to 25 repetitions of a command, but your rate limit is
        set to 5, then only the first 5 will execute.
      </p>
      <Image src='/images/support_command_banning.png' />
      <br />
      <p>
        Commands that allow for user input (both if they are a global command, or a custom command), will have options for banning input items from being given. Commands that have this
        functionality will show the additional input elements within the command configuration window. You can add as many items as you want to this list, and prevent the command from
        using the blocked user input.
      </p>
    </React.Fragment>
  )
};

export default Commands;
